import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

type Dog = {
  id: string;
  name: string;
  breed: string | null;
  sex: string | null;
  birth_date: string | null;
  neutered: boolean | null;
  chemically_neutered: boolean | null;
  direct_booking_allowed: boolean | null;
  capacity_exception: boolean | null;
  active: boolean | null;
};

type Reservation = {
  id: string;
  booking_type: string;
  start_date: string;
  end_date: string;
  status: string;
  total_amount: number | null;
  amount_paid: number | null;
};

type Family = {
  id: string;
  user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  moneybird_contact_id: string | null;
  notes_private: string | null;
  account_created_at: string | null;
  account_email_confirmed_at: string | null;
  account_last_sign_in_at: string | null;
  dogs: Dog[];
  reservations: Reservation[];
};

type InviteResult = {
  family_id: string;
  email: string;
  name: string;
  status: 'sent' | 'skipped' | 'error';
  message?: string;
};

const APPROVED_INVITE_CUSTOMERS = [
  { name: "Annet de Vries", email: "annettoxopeus68@gmail.com", familyId: "27d94ab1-468b-4756-ab47-cbed217a1755" },
  { name: "Arno Rietveld", email: "arno-rietveld@ziggo.nl", familyId: "bc9b8cee-82c9-4793-a760-164edc23088f" },
  { name: "Babs Mouton", email: "jbtk80@gmail.com", familyId: "9b7d4c2b-58a7-4684-8b24-02f5bc242707" },
  { name: "Bonita en Benjamin de Gelder", email: "b.gelder@upcmail.nl", familyId: "3fe05863-2903-4423-8c57-2b7a2f54cb71" },
  { name: "Cathrin Paqué", email: "c.paque66@gmail.com", familyId: "4e5f22cf-7e09-4365-ad2c-4811f813a390" },
  { name: "Chantal en Jan-Willem Huisman", email: "jhuisman2@gmail.com", familyId: "7b7f1e55-adf1-4c13-8ff2-a54d4f0e4c1d" },
  { name: "Chantal Peerbooms", email: "fampeerbooms@gmail.com", familyId: "dcfbb99b-7db1-4abe-b3a4-a55f59937225" },
  { name: "Claudia Emck", email: "claudia.emck@gmail.com", familyId: "f6c8135f-67db-4b7c-bb44-6cef627655ac" },
  { name: "Daniëlle van der Horst", email: "danielle@horstvander.nl", familyId: "9376a35b-50f5-447c-ab1a-a47a9ce52dfd" },
  { name: "Dirk en Caroline de Heer", email: "dirkheer1959@gmail.com", familyId: "ae8b0837-50db-45b0-8d8e-d3da640803dc" },
  { name: "Edwin & Esther Dumont", email: "edesdumont@gmail.com", familyId: "c9858fcc-c0a6-4d5f-87be-9c97b2d498b6" },
  { name: "Eline Klap", email: "wij@familieklap.nl", familyId: "5ea18e9b-5b17-4860-b432-cca644624d6d" },
  { name: "Ermgard & Rajiv Ramphal", email: "ermgardsalij@hotmail.com", familyId: "f619c5d6-914c-4117-9ff5-3a5d68a6fc5a" },
  { name: "Henri en Petra Maillé", email: "maille@upcmail.nl", familyId: "bcefa43b-6667-431d-ba5e-7365a540550b" },
  { name: "Ingrid Blok", email: "ingrid.blok@hotmail.com", familyId: "9410fbc9-855c-4945-91e1-652159681869" },
  { name: "Janneke Hermes", email: "janneke_hermes@hotmail.com", familyId: "01f8fe21-5206-403c-a0a6-9f0f66121ce8" },
  { name: "Jaqueline de Block", email: "deblockjacqueline@gmail.com", familyId: "a0542ee2-ea3e-4b97-a39a-422e50710eaa" },
  { name: "Judith Peters", email: "jempeters@live.nl", familyId: "7a407485-ffc1-40ec-acd8-f2180a740bbf" },
  { name: "Karin de Vries", email: "johnenkarin@gmail.com", familyId: "3dffd3d4-359b-48a1-8ca3-03f5ac8441ad" },
  { name: "Kim en Kevin van der Ploeg", email: "langeraac_07@hotmail.com", familyId: "ed7c032d-62a6-4761-9ea6-2b6591c11eab" },
  { name: "Laurien Boelhouwer - Cools", email: "laurien@boelhouwer.org", familyId: "2dd8ed75-7390-4938-8dc6-1ae2f563b0fa" },
  { name: "Leontine Schoonderwoerd", email: "leontine.schoonderwoerd@hotmail.com", familyId: "17cea1bb-a884-414b-9a65-f1f5346da0ae" },
  { name: "Lis Briët", email: "l.j.vanpoelgeest@hotmail.com", familyId: "d56dc0b6-befa-49b5-9b4c-ebff5c834960" },
  { name: "Manon Coppoolse", email: "manonc88@gmail.com", familyId: "8b369bc8-afaf-4439-80ee-72a4ac525fe0" },
  { name: "Marianne Stam", email: "mrst@ziggo.nl", familyId: "d264629d-574e-4ee9-88a5-739c0bc26c71" },
  { name: "Marieke von Geldern", email: "marieke.van.rijnsoever@xs4all.nl", familyId: "f31d0722-4b4f-41b2-bbaf-45c8b355ef53" },
  { name: "Mariken Bouhas-Janssen", email: "mariken_janssen@hotmail.nl", familyId: "6315fbec-3138-4d96-bab1-48104249e40d" },
  { name: "Marja & Peter Vonk", email: "plvonk@hotmail.com", familyId: "60482636-84f7-4423-a224-defdcbe96527" },
  { name: "Marjolein van Lith-buring", email: "marjoleinburing@gmail.com", familyId: "057bf146-3ebc-47c1-97f7-cb6a18f99b16" },
  { name: "Melanie Coffeng-Wijnbergen", email: "melanie.wijnbergen@xs4all.nl", familyId: "7b752468-b53c-41b6-83e4-d7eaf71a7e69" },
  { name: "Michiel en Sita den Boer - Humalda", email: "sita.humalda@impalafoundation.com", familyId: "10fcc263-7c7e-4611-af37-11c35dffe783" },
  { name: "Miranda Heule", email: "mtbjanssen@gmail.com", familyId: "572cff5a-3128-4dff-a91c-51d36b2547d0" },
  { name: "Monique van Vliet", email: "m.vanvliet@plusservices.nl", familyId: "4ce9104e-b546-48cb-ac07-30bcc9752b00" },
  { name: "Natalie Hokke", email: "nataliehokke@outlook.com", familyId: "e18f020f-30ed-4267-9e56-61f45d7a3589" },
  { name: "Niels & Kirsten Bresser/Ammerlaan", email: "kirstena1997@hotmail.com", familyId: "f8ed4307-fe63-452a-8140-c40e61ff7840" },
  { name: "Paula Plas en Michiel van Geest", email: "geestm@dds.nl", familyId: "dc24c3e5-5724-418d-8da4-3b0b962b878d" },
  { name: "Remco & Sonja Zwiers", email: "son_rem@hotmail.com", familyId: "86f136ae-3ed3-4062-9ee8-26037d3c0d43" },
  { name: "Richard en Janet van de Minkelis", email: "uma3@xs4all.nl", familyId: "851e768e-b317-48e8-86e0-bb1b288e90f9" },
  { name: "Robin Mooldijk", email: "robin.mooldijk@hotmail.com", familyId: "820ef76a-31ba-4ee8-8a21-d7e0d0e915fd" },
  { name: "Sabine Hochstenbach", email: "sabine.hochstenbach@gmail.com", familyId: "956defe7-031f-4fce-bbdb-0c67f8e069c6" },
  { name: "Saskia Leenders", email: "saskia.leenders.72@outlook.com", familyId: "e9af1739-a4d7-408c-98cc-c83d04d4bd59" },
  { name: "Ton Schutte", email: "ton.schutte@me.com", familyId: "43642252-13f4-4f0f-b282-a591173401cc" }
] as const;

const money = (value: number | null) =>
  value === null
    ? '—'
    : new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(Number(value));

const date = (value: string) =>
  new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(`${value}T12:00:00`));

const ownerName = (family: Family) =>
  [family.first_name, family.last_name]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Naam ontbreekt';

const statusLabel = (status: string) =>
  ({
    confirmed: 'Bevestigd',
    option: 'Optie',
    cancelled: 'Geannuleerd',
    completed: 'Afgerond',
    expired: 'Verlopen',
    cancel_pending_payment: 'Wacht op betaling'
  }[status] ?? status);

const yesNo = (value: boolean | null, yes = 'Ja', no = 'Nee') =>
  value === null ? 'Onbekend' : value ? yes : no;

export const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Family | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResults, setInviteResults] = useState<InviteResult[]>([]);
  const [activationBusyId, setActivationBusyId] = useState<string | null>(null);
  const [singleInviteBusyId, setSingleInviteBusyId] = useState<string | null>(null);
  const [activationSuccess, setActivationSuccess] = useState('');
  const [activationError, setActivationError] = useState('');

  useEffect(() => {
    loadFamilies();
  }, []);

  async function loadFamilies() {
    if (!supabase) {
      setError('De Supabase-verbinding is niet beschikbaar.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const { data, error: loadError } = await supabase.rpc(
      'admin_list_customers_private_v1'
    );

    if (loadError) {
      setError(`Klanten konden niet worden geladen: ${loadError.message}`);
      setFamilies([]);
    } else {
      setFamilies((data ?? []) as Family[]);
    }

    setLoading(false);
  }

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return families;

    return families.filter(family => {
      const haystack = [
        ownerName(family),
        family.email,
        family.phone,
        family.street,
        family.postal_code,
        family.city,
        ...(family.dogs ?? []).map(dog => dog.name)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [families, query]);

  const activeAccounts = families.filter(f => !!f.account_last_sign_in_at).length;
  const moneybirdContacts = families.filter(f => !!f.moneybird_contact_id).length;
  const reservationsThatCount = (family: Family) =>
    (family.reservations ?? []).filter(
      reservation =>
        reservation.status !== 'cancelled' &&
        reservation.status !== 'expired'
    );

  const familiesWithReservations = families.filter(
    family => reservationsThatCount(family).length > 0
  ).length;

  const activeDogs = (family: Family) =>
    (family.dogs ?? []).filter(dog => dog.active !== false);

  const sortedReservations = (family: Family) => {
    const today = new Date().toISOString().slice(0, 10);
    const reservations = [...(family.reservations ?? [])];

    const upcoming = reservations
      .filter(reservation => reservation.end_date >= today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));

    const history = reservations
      .filter(reservation => reservation.end_date < today)
      .sort((a, b) => b.start_date.localeCompare(a.start_date));

    return [...upcoming, ...history];
  };

  const accountStatus = (family: Family) => {
    if (!family.user_id) {
      return { label: 'Nog niet gekoppeld', color: 'default' as const };
    }
    if (family.account_last_sign_in_at) {
      return { label: 'Mijn Pippaya actief', color: 'success' as const };
    }
    return { label: 'Uitgenodigd', color: 'warning' as const };
  };

  const approvedInviteState = useMemo(() => {
    const byId = new Map(families.map(family => [family.id, family]));
    const problems: string[] = [];
    const pending: Array<{ family: Family; name: string; email: string; familyId: string }> = [];

    for (const approved of APPROVED_INVITE_CUSTOMERS) {
      const family = byId.get(approved.familyId);

      if (!family) {
        problems.push(`${approved.name}: klantprofiel niet gevonden.`);
        continue;
      }

      const actualEmail = (family.email ?? '').trim().toLowerCase();
      if (actualEmail !== approved.email) {
        problems.push(
          `${approved.name}: e-mailadres wijkt af (${family.email || 'geen e-mailadres'}).`
        );
        continue;
      }

      if (!family.user_id) {
        pending.push({
          family,
          name: approved.name,
          email: approved.email,
          familyId: approved.familyId
        });
      }
    }

    return { pending, problems };
  }, [families]);

  const alreadyLinkedApprovedCount =
    APPROVED_INVITE_CUSTOMERS.length - approvedInviteState.pending.length;

  async function inviteExistingCustomer(family: Family) {
    if (!supabase || singleInviteBusyId || activationBusyId) return;

    if (family.user_id || !family.email) {
      setActivationError(
        'Voor deze klant kan geen Mijn Pippaya-uitnodiging worden verstuurd.'
      );
      setActivationSuccess('');
      return;
    }

    setSingleInviteBusyId(family.id);
    setActivationError('');
    setActivationSuccess('');

    const redirectTo = `${window.location.origin}/account-activeren`;

    const { data, error: invokeError } = await supabase.functions.invoke(
      'pippaya-invite-existing-customer-v1',
      {
        body: {
          family_id: family.id,
          redirect_to: redirectTo
        }
      }
    );

    if (invokeError) {
      setActivationError(invokeError.message);
      setSingleInviteBusyId(null);
      return;
    }

    if (data?.error) {
      setActivationError(String(data.error));
      setSingleInviteBusyId(null);
      return;
    }

    setActivationSuccess(
      data?.message ||
        `Mijn Pippaya-uitnodiging verstuurd naar ${family.email}.`
    );

    // Werk het geopende klantvenster meteen bij, zodat de knop direct
    // verandert naar “Nieuwe activatielink sturen”.
    if (data?.user_id) {
      setSelected(current =>
        current && current.id === family.id
          ? {
              ...current,
              user_id: String(data.user_id),
              account_created_at:
                current.account_created_at || new Date().toISOString()
            }
          : current
      );
    }

    await loadFamilies();
    setSingleInviteBusyId(null);
  }

  async function resendActivationLink(family: Family) {
    if (!supabase || activationBusyId) return;

    if (!family.user_id || family.account_last_sign_in_at || !family.email) {
      setActivationError(
        'Voor deze klant kan geen nieuwe activatielink worden verstuurd.'
      );
      setActivationSuccess('');
      return;
    }

    setActivationBusyId(family.id);
    setActivationError('');
    setActivationSuccess('');

    const redirectTo = `${window.location.origin}/account-activeren`;

    const { data, error: invokeError } = await supabase.functions.invoke(
      'pippaya-resend-activation-v1',
      {
        body: {
          family_id: family.id,
          redirect_to: redirectTo
        }
      }
    );

    if (invokeError) {
      setActivationError(invokeError.message);
      setActivationBusyId(null);
      return;
    }

    if (data?.error) {
      setActivationError(String(data.error));
      setActivationBusyId(null);
      return;
    }

    setActivationSuccess(
      data?.message ||
        `Nieuwe activatielink verstuurd naar ${family.email}.`
    );
    setActivationBusyId(null);
  }

  async function sendApprovedInvitations() {
    if (!supabase || sendingInvites || approvedInviteState.problems.length > 0) return;
    if (!approvedInviteState.pending.length) return;

    setSendingInvites(true);
    setInviteResults([]);

    const payload = approvedInviteState.pending.map(item => ({
      family_id: item.familyId,
      email: item.email,
      name: item.name
    }));

    const { data, error: invokeError } = await supabase.functions.invoke(
      'pippaya-existing-customer-bulk-invite-v1',
      {
        body: { items: payload }
      }
    );

    if (invokeError) {
      setInviteResults(
        payload.map(item => ({
          ...item,
          status: 'error' as const,
          message: invokeError.message
        }))
      );
      setSendingInvites(false);
      return;
    }

    setInviteResults((data?.results ?? []) as InviteResult[]);
    await loadFamilies();
    setSendingInvites(false);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={2}
          alignItems={{ md: 'center' }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Pippaya Hondenopvang · beheer
            </Typography>
            <Typography variant="h3">Klanten</Typography>
            <Typography color="text.secondary">
              Contactgegevens, honden, reserveringen en Mijn Pippaya-koppeling.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={<MarkEmailUnreadRoundedIcon />}
              onClick={() => {
                setInviteResults([]);
                setInviteDialogOpen(true);
              }}
              disabled={loading}
            >
              Uitnodigingen versturen ({approvedInviteState.pending.length})
            </Button>

            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/beheer')}
            >
              Terug naar beheer
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mt: 2.5 }}>
          Bij iedere klant zie je nu het verschil tussen nog niet gekoppeld,
          uitgenodigd en Mijn Pippaya actief. “Actief” betekent dat er al een
          eerste aanmelding bij Supabase is geregistreerd.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 2.5 }}
        >
          <Card sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <GroupsRoundedIcon />
              <Box>
                <Typography variant="h5">{families.length}</Typography>
                <Typography color="text.secondary">Klanten</Typography>
              </Box>
            </Stack>
          </Card>

          <Card sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <AccountCircleRoundedIcon />
              <Box>
                <Typography variant="h5">{activeAccounts}</Typography>
                <Typography color="text.secondary">Mijn Pippaya actief</Typography>
              </Box>
            </Stack>
          </Card>

          <Card sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <ReceiptLongRoundedIcon />
              <Box>
                <Typography variant="h5">{moneybirdContacts}</Typography>
                <Typography color="text.secondary">Met Moneybird-contact</Typography>
              </Box>
            </Stack>
          </Card>

          <Card sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <PetsRoundedIcon />
              <Box>
                <Typography variant="h5">{familiesWithReservations}</Typography>
                <Typography color="text.secondary">Met reservering(en)</Typography>
              </Box>
            </Stack>
          </Card>
        </Stack>

        <TextField
          fullWidth
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Zoek op klant, e-mail, telefoon, plaats of hond..."
          sx={{ mt: 2.5 }}
          InputProps={{
            startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Klanten laden...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 2.5 }}>
            <Typography color="text.secondary">
              {filtered.length} van {families.length} klanten
            </Typography>

            {filtered.map(family => {
              const dogs = activeDogs(family);
              const reservations = reservationsThatCount(family);
              const nextReservation = [...reservations]
                .filter(r => r.end_date >= new Date().toISOString().slice(0, 10))
                .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];

              return (
                <Paper
                  key={family.id}
                  variant="outlined"
                  sx={{ p: 2, boxShadow: 'none' }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography variant="h6">{ownerName(family)}</Typography>
                        <Chip
                          size="small"
                          color={accountStatus(family).color}
                          label={accountStatus(family).label}
                        />
                        {family.moneybird_contact_id && (
                          <Chip size="small" variant="outlined" label="Moneybird" />
                        )}
                      </Stack>

                      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        {family.email || 'Geen e-mailadres'}
                        {family.phone ? ` · ${family.phone}` : ''}
                        {family.city ? ` · ${family.city}` : ''}
                      </Typography>

                      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1.25 }}>
                        {dogs.map(dog => (
                          <Chip
                            key={dog.id}
                            size="small"
                            icon={<PetsRoundedIcon />}
                            label={dog.name}
                            color={dog.capacity_exception ? 'secondary' : 'default'}
                            variant={dog.direct_booking_allowed === false ? 'outlined' : 'filled'}
                          />
                        ))}
                        {!dogs.length && (
                          <Chip size="small" label="Geen actieve hond" />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {reservations.length} reservering(en)
                        {nextReservation
                          ? ` · eerstvolgende ${date(nextReservation.start_date)}`
                          : ' · geen toekomstige reservering'}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      onClick={() => {
                        setActivationSuccess('');
                        setActivationError('');
                        setSelected(family);
                      }}
                      sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
                    >
                      Bekijken
                    </Button>
                  </Stack>
                </Paper>
              );
            })}

            {!filtered.length && (
              <Alert severity="info">Geen klanten gevonden met deze zoekterm.</Alert>
            )}
          </Stack>
        )}
      </Container>

      <Dialog
        open={inviteDialogOpen}
        onClose={() => {
          if (!sendingInvites) setInviteDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Uitnodigingen Mijn Pippaya</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Deze lijst is hard gekoppeld aan de definitieve selectie van 42 goedgekeurde
              klanten. Klanten buiten die lijst kunnen vanuit deze functie niet worden
              uitgenodigd. Robin wordt automatisch overgeslagen omdat zijn account al gekoppeld
              is.
            </Alert>

            {approvedInviteState.problems.length > 0 && (
              <Alert severity="error">
                Er zijn verschillen gevonden. Er wordt niets verstuurd totdat dit is opgelost.
                <Box component="ul" sx={{ mb: 0 }}>
                  {approvedInviteState.problems.map(problem => (
                    <li key={problem}>{problem}</li>
                  ))}
                </Box>
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, boxShadow: 'none' }}>
              <Typography fontWeight={800}>
                {approvedInviteState.pending.length} uitnodiging(en) klaar om te versturen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {alreadyLinkedApprovedCount} van de 42 goedgekeurde klanten zijn al gekoppeld of
                eerder uitgenodigd.
              </Typography>
            </Paper>

            {!!approvedInviteState.pending.length && (
              <Stack spacing={0.75}>
                {approvedInviteState.pending.map(item => (
                  <Paper
                    key={item.familyId}
                    variant="outlined"
                    sx={{ p: 1.25, boxShadow: 'none' }}
                  >
                    <Typography fontWeight={700}>{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.email}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}

            {!!inviteResults.length && (
              <>
                <Divider />
                <Typography variant="h6">Resultaat</Typography>
                <Stack spacing={0.75}>
                  {inviteResults.map(result => (
                    <Alert
                      key={`${result.family_id}-${result.email}`}
                      severity={
                        result.status === 'sent'
                          ? 'success'
                          : result.status === 'skipped'
                            ? 'info'
                            : 'error'
                      }
                    >
                      <b>{result.name}</b> · {result.email}
                      {result.message ? ` · ${result.message}` : ''}
                    </Alert>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInviteDialogOpen(false)}
            disabled={sendingInvites}
          >
            Sluiten
          </Button>
          <Button
            variant="contained"
            startIcon={<MarkEmailUnreadRoundedIcon />}
            onClick={sendApprovedInvitations}
            disabled={
              sendingInvites ||
              approvedInviteState.problems.length > 0 ||
              approvedInviteState.pending.length === 0
            }
          >
            {sendingInvites
              ? 'Bezig met versturen...'
              : `Verstuur ${approvedInviteState.pending.length} uitnodigingen`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selected}
        onClose={() => {
          if (!activationBusyId && !singleInviteBusyId) {
            setSelected(null);
            setActivationSuccess('');
            setActivationError('');
          }
        }}
        fullWidth
        maxWidth="md"
      >
        {selected && (
          <>
            <DialogTitle>{ownerName(selected)}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Box>
                  <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                    <Typography variant="h6">Contact</Typography>
                    <Chip
                      size="small"
                      color={accountStatus(selected).color}
                      label={accountStatus(selected).label}
                    />
                  </Stack>
                  <Typography sx={{ mt: 1 }}>
                    <b>E-mail:</b> {selected.email || '—'}
                  </Typography>
                  <Typography>
                    <b>Telefoon:</b> {selected.phone || '—'}
                  </Typography>
                  <Typography>
                    <b>Adres:</b>{' '}
                    {[selected.street, selected.postal_code, selected.city]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </Typography>
                  <Typography>
                    <b>Moneybird:</b>{' '}
                    {selected.moneybird_contact_id
                      ? `gekoppeld (${selected.moneybird_contact_id})`
                      : 'geen bestaand contact-id'}
                  </Typography>
                  <Typography>
                    <b>Mijn Pippaya:</b>{' '}
                    {selected.account_last_sign_in_at
                      ? `actief · laatste aanmelding ${new Intl.DateTimeFormat('nl-NL', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }).format(new Date(selected.account_last_sign_in_at))}`
                      : selected.user_id
                        ? 'uitgenodigd, nog geen eerste aanmelding geregistreerd'
                        : 'nog geen account gekoppeld'}
                  </Typography>

                  {activationSuccess && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                      {activationSuccess}
                    </Alert>
                  )}

                  {activationError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {activationError}
                    </Alert>
                  )}

                  {!selected.user_id &&
                    selected.email &&
                    activeDogs(selected).length > 0 && (
                      <Button
                        variant="contained"
                        startIcon={<MarkEmailUnreadRoundedIcon />}
                        sx={{ mt: 1.5 }}
                        disabled={
                          singleInviteBusyId === selected.id ||
                          !!activationBusyId
                        }
                        onClick={() =>
                          void inviteExistingCustomer(selected)
                        }
                      >
                        {singleInviteBusyId === selected.id
                          ? 'Uitnodiging versturen...'
                          : 'Mijn Pippaya uitnodiging sturen'}
                      </Button>
                    )}

                  {selected.user_id &&
                    !selected.account_last_sign_in_at &&
                    selected.email && (
                      <Button
                        variant="outlined"
                        startIcon={<MarkEmailUnreadRoundedIcon />}
                        sx={{ mt: 1.5 }}
                        disabled={activationBusyId === selected.id}
                        onClick={() => void resendActivationLink(selected)}
                      >
                        {activationBusyId === selected.id
                          ? 'Activatielink versturen...'
                          : 'Nieuwe activatielink sturen'}
                      </Button>
                    )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Honden
                  </Typography>
                  <Stack spacing={1}>
                    {activeDogs(selected).map(dog => (
                      <Paper key={dog.id} variant="outlined" sx={{ p: 1.5, boxShadow: 'none' }}>
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                          <Typography fontWeight={800}>{dog.name}</Typography>
                          {dog.capacity_exception && (
                            <Chip size="small" color="secondary" label="Uitzonderingshond" />
                          )}
                          {dog.direct_booking_allowed === false && (
                            <Chip size="small" color="warning" label="Niet direct boekbaar" />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {dog.breed || 'Ras onbekend'} · {dog.sex || 'geslacht onbekend'}
                          {dog.birth_date ? ` · geboren ${date(dog.birth_date)}` : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Gecastreerd/gesteriliseerd: {yesNo(dog.neutered)}
                          {dog.chemically_neutered
                            ? ' · chemisch gecastreerd: ja'
                            : ''}
                        </Typography>
                      </Paper>
                    ))}
                    {!activeDogs(selected).length && (
                      <Typography color="text.secondary">Geen actieve honden.</Typography>
                    )}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Reserveringen
                  </Typography>
                  <Stack spacing={1}>
                    {sortedReservations(selected).map(reservation => (
                      <Paper
                        key={reservation.id}
                        variant="outlined"
                        sx={{ p: 1.5, boxShadow: 'none' }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Box>
                            <Typography fontWeight={800}>
                              {date(reservation.start_date)}
                              {reservation.end_date !== reservation.start_date
                                ? ` t/m ${date(reservation.end_date)}`
                                : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {reservation.booking_type === 'dagopvang'
                                ? 'Dagopvang'
                                : 'Logeren'}{' '}
                              · {statusLabel(reservation.status)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: { sm: 'right' } }}>
                            <Typography fontWeight={800}>
                              {money(reservation.total_amount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Betaald: {money(reservation.amount_paid)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                    {!selected.reservations?.length && (
                      <Typography color="text.secondary">Geen reserveringen.</Typography>
                    )}
                  </Stack>
                </Box>

                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6">Interne notities</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      Alleen zichtbaar voor Pippaya-beheer.
                    </Typography>
                    <Typography sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                      {selected.notes_private || 'Geen interne notitie.'}
                    </Typography>
                  </Box>
                </>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={!!activationBusyId || !!singleInviteBusyId}
                onClick={() => {
                  setSelected(null);
                  setActivationSuccess('');
                  setActivationError('');
                }}
              >
                Sluiten
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
